import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { usePrevious } from 'react-delta';
import { cloneDeep } from 'lodash';
import { isNumber, isString } from 'lodash';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { FETCH_ORIGIN } from 'common/types';
import { LogEntry, SystemField } from 'common/graphqlTypes';

import { quote } from 'common/util/string';

import { settingsState } from 'components/common/Managers/SettingsManager.state';
import { searchParamState } from 'components/common/Managers/RouteStateManager.state';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import { logToFetchState } from 'components/search/SearchLogsPage/LogResultsContainer.hooks';
import useCopyToClipboard from 'hooks/useCopyToClipboard';

import Card from 'components/common/Card/Card';
import Table from 'components/common/Table/Table';

import { timezoneMoment } from 'common/util/time';
import FacetValue from 'components/common/FacetValue/FacetValue';
import FacetFilter from 'components/common/FacetFilter/FacetFilter';

import { Schema } from 'schemas';

import './LogDetails.scss';
import LogDetailsHeader from './LogDetailsHeader';
import LogDetailsActionButtons from './LogDetailsActionButtons';

export type LogDetailsProps = {
  onClose:                 () => void;
  onTogglePinned?:         () => void;
  isPinned?:               boolean,
  logEntry?:               LogEntry | null;
  continuationToken?:      string | null;
  enableLogSearchActions?: boolean;
  schema?:                 Schema;
};

function LogDetails({
  onClose,
  onTogglePinned,
  logEntry,
  isPinned,
  continuationToken,
  enableLogSearchActions = true,
}: LogDetailsProps): JSX.Element {

  const [ fieldFilter, setFieldFilter ] = useState('');
  const settings = useRecoilValue(settingsState);
  const timezone = useRecoilValue(timezoneState);
  const filter = useRecoilValue(searchParamState(K.URL_PARAM.FILTER));
  const copy = useCopyToClipboard();

  const prevLogEntry = usePrevious(logEntry);
  const prevContinuationToken = usePrevious(continuationToken);
  const setLogToFetch = useSetRecoilState(logToFetchState);

  // if the current continuationToken is falsy and this component is rendering, it means the drawer is closing and it
  // should continue to display the stale data while it closes.
  if (!continuationToken) {
    continuationToken = prevContinuationToken;
    logEntry = prevLogEntry;
  }

  // Only attempt to populate the drawer content if there is valid data.
  if (!logEntry || !continuationToken) {
    return (<></>);
  }

  const eventFields = cloneDeep(logEntry.values);
  if (logEntry.severity) {
    eventFields.severity = logEntry.severity;
  }

  const serverFields = cloneDeep(logEntry.serverInfo);
  if (logEntry.threadId) {
    serverFields.threadId = logEntry.threadId;
  }

  const tableOptions = {
    noHeaders: true,
    rowClass:  'LogDetails__Row',
    itemClass: 'LogDetails__Item',
  };

  // event and server field tables have same headers
  const headers = [
    {
      field:    'facet',
      autoSize: true,
      maxWidth: 220,
    },
    {
      field: 'value',
    },
    {
      field: 'filter',
      width: 90,
    },
  ];

  // create table cells out of event data
  const getDataRows = (
    fields:     string[],
    data:       Record<string, string | number | boolean>,
    omitField = false,
  ) => fields.map((field, index) => {
    const value = data[field];
    const isTruncated = logEntry?.facetsWithTruncatedValues.includes(field);
    const valueEl = (
        <FacetValue
          field={field}
          value={String(value)}
          filter={filter}
          settings={settings}
          doLinkify
          doPrettyPrint
          isTruncated={isTruncated}
          continuationToken={continuationToken ?? ''}
        />
    );
    const facetFilter = (
        <FacetFilter
          field={field}
          value={isString(value) ? quote(value, false, true) : value}
          valueIsNumeric={isNumber(value)}
          onSearch={onClose}
          onCopy={() => copy(String(value), 'value')}
          index={index}
        />
    );
    return omitField ?
      {
        value:  valueEl,
        filter: facetFilter,
      } : {
        facet:  field,
        value:  valueEl,
        filter: facetFilter,
      };
  });

  // show message (if available) in a box

  const message = logEntry && logEntry.values && logEntry.values.message;
  // below is for testing; most events on localhost lack a message field
  /*
  const message = '2020-02-20 00:29:19.529Z [linux_system_metrics()] net.stat.tcp.reording 587 '
                + 'detectedby="timestamp"';
  const message = '{"ts":1582231394367,"elapsed":4,"spanId":"82cb3bb2-0170-4e0a-8532-f507b935b136",'
            + '"action":"finish","operation":"queryTable","tags":{"request":"x826706988",'
            + '"query":"SELECT * FROM events WHERE user = * ORDER BY date DESC limit 10"},'
            + '"baggage":{"traceId":"cc6f3bc7-cd90-46b5-ae2b-7b4161f42e81",'
            + '"reporter.spanId":"82cb3bb2-0170-4e0a-8532-f507b935b136"},'
            + '"references":{"child_of":"6e088429-2e80-4828-b1b2-2e428c81d01d"}}';
  */

  let messageBox = null;
  if (message) {
    const messageHeaders = [
      {
        field: 'value',
      },
      {
        field: 'filter',
        width: 80, // doesn't have < >, can be narrower
      },
    ];
    const messageRows = getDataRows([ K.FIELD.MESSAGE ], { message }, true);
    messageBox = (
      <Card className="LogDetails__MessageCard" bodyClass="LogDetails__Message">
        <Table headers={messageHeaders} data={messageRows} options={tableOptions} />
      </Card>
    );
  }

  // regular event facets
  let eventFieldList = Object.keys(eventFields);
  eventFields[SystemField.timestamp] = timezoneMoment(timezone)(logEntry.timestamp / 1000000).format('MMM D · h:mm:ss.SSS A');
  eventFieldList.unshift(SystemField.timestamp);
  eventFieldList = eventFieldList
    .filter((key) => eventFields[key] != null && key !== K.FIELD.MESSAGE && key.toLowerCase().indexOf(fieldFilter.toLowerCase()) !== -1)
    .sort();
  const eventRows = getDataRows(eventFieldList, eventFields);

  if (logEntry.facetsNumberLimitExceeded) {
    const moreEl = (
      <span>
        <span className="LogLine__MoreAttributes">&hellip;</span>
        <span data-is-more-link="true" className="link" onClick={() => setLogToFetch({ continuationToken: continuationToken || '', origin: FETCH_ORIGIN.ATTRIBUTES })}>more attributes</span>
      </span>
    );
    eventRows.push({
      value: moreEl,
      filter: <span></span>,
    });
  }

  // system fields (serverHost, etc)
  const serverFieldList = Object.keys(serverFields)
    .filter((key) => serverFields[key] != null && key !== K.FIELD.MESSAGE && key.toLowerCase().indexOf(fieldFilter.toLowerCase()) !== -1)
    .sort();
  const serverRows = getDataRows(serverFieldList, serverFields);

  const serverFieldsHeader = (serverRows.length === 0)
    ? null
    : <div className="LogDetails__ServerFieldsTitle" data-test-id={ELEMENT.inspectFieldsDrawerServerFieldsTitle}>Server Fields</div>;

  return (
    <>
      <LogDetailsHeader
        isPinned={isPinned}
        onClose={onClose}
        onTogglePinned={onTogglePinned}
      />
      <LogDetailsActionButtons
        logEntry={logEntry}
        continuationToken={continuationToken}
        enableLogSearchActions={enableLogSearchActions}
        fieldFilter={fieldFilter}
        onClose={onClose}
        setFieldFilter={setFieldFilter}
      />
      <div className="LogDetails__FieldsContainer">
        { messageBox }
        <div className="LogDetails__FieldsArea">
          <Table headers={headers} data={eventRows} options={tableOptions} />
        </div>
        {serverFieldsHeader}
        <div className="LogDetails__FieldsArea">
          <Table headers={headers} data={serverRows} options={tableOptions} />
        </div>
      </div>
    </>
  );
}

export default LogDetails;
